// @src/pre-deployment/src/components/ClozeExercise.js v2.1
// - Updated for ChapterContainer compatibility
// - Uses props from ChapterContainer instead of useParams
// - Fixed navigation handling for new App.js structure

import React, { useState, useEffect, useRef } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { GrammarTracking, ChapterProgress } from '../../data/tracking';
import { useClozeExercises, useSessionProgressActions } from '../../data/useDataService';
import { TrackingV2Service } from '../../tracking/trackingService.js';

const ClozeExercise = ({ _moduleKey, _moduleConfig, navigation, _modulesOrder, _moduleMap, level, chapter }) => {
    const { user } = useAuth();

    const sessionType = 'Learn';
    const moduleType = 'cloze';

    // Game state
    const [gameState, setGameState] = useState('setup'); // setup, playing, completed
    const [currentExercise, setCurrentExercise] = useState(null);
    const [userAnswer, setUserAnswer] = useState('');
    const [showResult, setShowResult] = useState(false);
    // Unused inlineMessage removed to satisfy lint
    const timerRef = useRef(null);
    const [isCorrect, setIsCorrect] = useState(null);
    const [score, setScore] = useState({ correct: 0, total: 0 });
    const [currentDifficulty, setCurrentDifficulty] = useState('simple');
    const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
    const [_exerciseResults, setExerciseResults] = useState([]);
    const [usedExercises, setUsedExercises] = useState(new Set());
    const [completedExercises, setCompletedExercises] = useState(new Set());
    const [exerciseNumber, setExerciseNumber] = useState(1);
    const [showHint, setShowHint] = useState(false);
    const startTimeRef = useRef(null);
    const questionStartTimeRef = useRef(null);

    // Data fetching with fallback
    const {
        data: fetchedExercises,
        loading: exercisesLoading,
        error: exercisesError
    } = useClozeExercises(level, chapter, 'Korea_University');

    // Enhanced fallback data with new structure
    const fallbackExercises = [
        {
            id: 'PA_01',
            type: 'fill_blank_conjugation',
            category: 'Production Accuracy',
            subcategory: 'Accuracy Rate',
            instruction: 'Complete the sentence by conjugating the verb in parentheses into the progressive form.',
            question: '지금 친구가 무엇을 __________? (먹다)',
            hint: 'Change \'먹다\' to mean \'is eating\'. Use the polite ending.',
            answer: '먹고 있어요',
            grammar_focus: 'progressive_construction',
            expected_input: 'full_verb_progressive',
            validation_rules: {
                accepted_answers: ['먹고 있어요'],
                key_components: ['먹고', '있어요']
            }
        },
        {
            id: 'CP_01',
            type: 'multiple_choice',
            category: 'Comprehension & Processing',
            subcategory: 'Grammaticality Judgment Score',
            instruction: 'Choose the sentence that best describes an action happening right now.',
            question: 'Which sentence means \'He is listening to music at this moment\'?',
            hint: 'Look for the ending that shows ongoing action.',
            options: [
                { "id": "A", "text": "그는 음악을 듣는다" },
                { "id": "B", "text": "그는 음악을 듣고 있다" }
            ],
            answer: 'B',
            explanation: '\'-고 있다\' shows the action is currently in progress',
            grammar_focus: 'progressive_vs_habitual',
            expected_input: 'single_letter'
        },
        {
            id: 'CP_02',
            type: 'true_false',
            category: 'Comprehension & Processing',
            subcategory: 'Grammaticality Judgment Score',
            instruction: 'Determine if this sentence is grammatically correct.',
            question: '나는 음악을 듣고 있다요.',
            hint: 'Check the ending. Is it polite form?',
            answer: 'X',
            explanation: 'The ending should be \'듣고 있어요\' for polite speech.',
            grammar_focus: 'grammaticality_judgment',
            expected_input: 'single_letter'
        }
    ];

    // Use fetched data if available, otherwise use fallback
    const exercises = (fetchedExercises && fetchedExercises.length > 0)
        ? fetchedExercises
        : fallbackExercises;

    const {
        saveExerciseAttemptWithSession
    } = useSessionProgressActions(user?.uid, parseInt(level), parseInt(chapter), sessionType);

    // Navigation is now passed as prop from ChapterContainer

    // Game configuration
    const [gameConfig, setGameConfig] = useState({
        sessionSize: 10
    });

    // Extract grammar point ID from exercise
    const extractGrammarId = (exercise) => {
        // Try to get grammar ID from required_grammar_point or grammar_id field
        if (exercise.required_grammar_point) {
            return exercise.required_grammar_point;
        }
        if (exercise.grammar_id) {
            return exercise.grammar_id;
        }
        // Default fallback based on grammar_focus
        if (exercise.grammar_focus) {
            return `ch${chapter}_gmr_001`;
        }
        return null;
    };

    // Get instruction text based on exercise type
    const getInstructionByType = (exerciseType) => {
        const instructions = {
            'fill_blank_conjugation': 'Complete the sentence using the progressive form (-고 있다)',
            'multiple_choice': 'Choose the correct answer from the options below',
            'true_false': 'Determine if the sentence is grammatically correct (O/X)',
            'error_correction': 'Identify and correct the grammatical error',
            'open_response': 'Write a complete sentence using the progressive form',
            'translation': 'Translate the sentence into Korean using -고 있다',
            'fill_blank_honorific': 'Complete using the appropriate honorific form'
        };
        return instructions[exerciseType] || 'Complete the exercise';
    };

    // Adaptive exercise selection
    const selectAdaptiveExercise = (_exerciseIndex) => {
        if (!exercises || exercises.length === 0) return null;

        // Filter exercises based on usage
        let candidateExercises = exercises.filter(ex =>
            !usedExercises.has(ex.id) &&
            !completedExercises.has(ex.id)
        );

        if (candidateExercises.length === 0) {
            candidateExercises = exercises.filter(ex =>
                !usedExercises.has(ex.id) &&
                !completedExercises.has(ex.id)
            );
        }

        if (candidateExercises.length === 0) {
            setUsedExercises(new Set());
            candidateExercises = exercises.filter(ex => !completedExercises.has(ex.id));
        }

        if (candidateExercises.length === 0) {
            return null;
        }

        return candidateExercises[Math.floor(Math.random() * candidateExercises.length)];
    };

    // Initialize component
    useEffect(() => {
        console.log('Cloze Exercise initialized for Level', level, 'Chapter', chapter);
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [level, chapter, exercises]);

    // End game (must be before loadNextExercise)
    const endGame = async () => {
        setGameState('completed');
        if (user?.uid) {
            const markModuleCompleted = async () => {
                try {
                    await TrackingV2Service.updateModuleProgress(
                        user.uid, level, chapter, 'clozeExercise', {
                        completion: true,
                        completionTime: new Date().toISOString(),
                        lastQuestionCompleted: score.total,
                        totalQuestions: score.total,
                        questionsCorrect: score.correct,
                        accuracy: score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0,
                        finalDifficulty: currentDifficulty
                    }, {
                        moduleType: 'clozeExercise',
                        completionMethod: 'user_finished',
                        finalScore: score,
                        accuracy: score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0,
                        finalDifficulty: currentDifficulty
                    }
                    );
                    console.log('✅ Module completion tracked successfully');
                } catch (error) {
                    console.error('❌ Failed to track module completion:', error);
                    ChapterProgress.updateModule(user.uid, level, chapter, 'Cloze', {
                        completion: true,
                        completion_time: new Date().toISOString(),
                        last_question_completed: score.total
                    });
                }
            };
            markModuleCompleted();
        }
    };

    // Load next exercise (must be before startGame)
    const loadNextExercise = (exerciseIndex) => {
        if (exerciseIndex >= gameConfig.sessionSize || !exercises) {
            endGame();
            return;
        }
        const selectedExercise = selectAdaptiveExercise(exerciseIndex);
        if (selectedExercise) {
            setCurrentExercise({ ...selectedExercise, index: exerciseIndex });
            const grammarId = extractGrammarId(selectedExercise);
            if (user && grammarId) {
                const markGrammarExposed = async () => {
                    try {
                        await TrackingV2Service.markGrammarExposed(
                            user.uid, level, chapter, grammarId, 'clozeExercise', {
                            exposureMethod: 'exercise_display',
                            exposedAt: new Date().toISOString(),
                            exerciseType: selectedExercise.type
                        }
                        );
                        console.log('✅ Grammar point marked as exposed');
                    } catch (error) {
                        console.error('❌ Failed to mark grammar as exposed:', error);
                        GrammarTracking.markExposed(
                            user.uid, parseInt(level), parseInt(chapter), sessionType, grammarId
                        ).catch(err => console.error('✗ Failed to mark grammar as exposed:', err));
                    }
                };
                markGrammarExposed();
            }
        }
        setUserAnswer('');
        setShowResult(false);
        setIsCorrect(null);
        setShowHint(false);
        questionStartTimeRef.current = Date.now();
    };

    // Start game
    const startGame = () => {
        if (!exercises || exercises.length === 0) {
            console.error('No exercises available');
            return;
        }

        // Track module start with new tracking system
        if (user?.uid) {
            const trackModuleStart = async () => {
                try {
                    await TrackingV2Service.updateModuleProgress(
                        user.uid, level, chapter, 'clozeExercise', {
                        userStarted: true,
                        startDate: new Date().toISOString(),
                        lastOpened: new Date().toISOString(),
                        interactionType: 'fill_in_blank',
                        totalExercises: exercises.length,
                        sessionSize: gameConfig.sessionSize
                    }, {
                        moduleType: 'clozeExercise',
                        interactionType: 'fill_in_blank',
                        startMethod: 'user_initiated',
                        sessionSize: gameConfig.sessionSize,
                        exerciseCount: exercises.length
                    }
                    );

                    console.log('✅ Module start tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track module start:', error);

                    // Fallback to old tracking system
                    ChapterProgress.updateModule(user.uid, level, chapter, 'Cloze', {
                        user_started: true,
                        start_date: new Date().toISOString(),
                        last_opened: new Date().toISOString(),
                        interaction_type: 'Fill-in-Blank'
                    });
                }
            };

            trackModuleStart();
        }

        setGameState('playing');
        setScore({ correct: 0, total: 0 });
        setCurrentDifficulty('simple');
        setConsecutiveCorrect(0);
        setExerciseResults([]);
        setUsedExercises(new Set());
        startTimeRef.current = Date.now();

        loadNextExercise(0);
    };

    // Validation function
    const validateAnswer = (userInput, exercise) => {
        const trimmedInput = userInput.trim();
        const correctAnswer = exercise.answer;

        // Direct match first
        if (trimmedInput.toLowerCase() === correctAnswer.toLowerCase()) {
            return { isCorrect: true, reason: 'exact_match', confidence: 1.0 };
        }

        // Check validation rules if available
        if (exercise.validation_rules && exercise.validation_rules.accepted_answers) {
            for (const accepted of exercise.validation_rules.accepted_answers) {
                if (trimmedInput.toLowerCase() === accepted.toLowerCase()) {
                    return { isCorrect: true, reason: 'accepted_variation', confidence: 0.9 };
                }
            }
        }

        return { isCorrect: false, reason: 'no_match', confidence: 0.0 };
    };

    // Handle user submission
    const handleSubmit = async () => {
        if (!currentExercise || !userAnswer.trim()) return;

        const responseTime = Date.now() - questionStartTimeRef.current;
        const validationResult = validateAnswer(userAnswer, currentExercise);
        const isAnswerCorrect = validationResult.isCorrect;

        // Update score
        const newScore = {
            correct: score.correct + (isAnswerCorrect ? 1 : 0),
            total: score.total + 1
        };
        setScore(newScore);

        // Store exercise result
        const exerciseResult = {
            index: currentExercise.index + 1,
            question: currentExercise.question,
            userAnswer: userAnswer.trim(),
            correctAnswer: currentExercise.answer,
            isCorrect: isAnswerCorrect,
            responseTime,
            category: currentExercise.category,
            subcategory: currentExercise.subcategory
        };

        setExerciseResults(prev => [...prev, exerciseResult]);

        // Mark exercise as used and completed
        setUsedExercises(prev => new Set([...prev, currentExercise.id]));
        setCompletedExercises(prev => new Set([...prev, currentExercise.id]));

        // Save progress to Firebase with new tracking system
        if (user?.uid) {
            const trackExerciseAttempt = async () => {
                try {
                    // Extract grammar ID and construct exercise ID
                    const grammarId = extractGrammarId(currentExercise);
                    const _exerciseId = grammarId
                        ? `${grammarId}_${currentExercise.id}`
                        : `cloze_${exerciseNumber}`;

                    // Add question attempt with new tracking system
                    await TrackingV2Service.addQuestionAttempt(
                        user.uid, level, chapter, 'clozeExercise', exerciseNumber, {
                        questionType: currentExercise.type || 'fill_blank_conjugation',
                        userResponse: userAnswer.trim(),
                        correctResponse: currentExercise.answer,
                        isCorrect: isAnswerCorrect,
                        responseTime: Math.round(responseTime / 1000),
                        responseOptions: currentExercise.options || [],
                        interactionType: 'fill_in_blank',
                        exerciseType: currentExercise.type,
                        category: currentExercise.category,
                        subcategory: currentExercise.subcategory || 'cloze_exercise',
                        grammarId: grammarId
                    }, {
                        questionType: currentExercise.type || 'fill_blank_conjugation',
                        interactionType: 'fill_in_blank',
                        exerciseType: currentExercise.type
                    }
                    );

                    // Update grammar mastery with new tracking system
                    if (grammarId) {
                        await TrackingV2Service.updateGrammarMastery(
                            user.uid, level, chapter, grammarId, 'clozeExercise', {
                            isCorrect: isAnswerCorrect,
                            userResponse: userAnswer.trim(),
                            correctResponse: currentExercise.answer,
                            responseTime: Math.round(responseTime / 1000),
                            interactionType: 'fill_in_blank',
                            exerciseType: currentExercise.type,
                            category: currentExercise.category
                        }, {
                            masteryType: 'cloze_exercise',
                            interactionType: 'fill_in_blank',
                            exerciseType: currentExercise.type
                        }
                        );
                    }

                    console.log('✅ Cloze exercise attempt tracked successfully');

                } catch (error) {
                    console.error('❌ Failed to track cloze exercise attempt:', error);

                    // Fallback to old tracking system
                    try {
                        const grammarId = extractGrammarId(currentExercise);
                        const exerciseId = grammarId
                            ? `${grammarId}_${currentExercise.id}`
                            : `cloze_${exerciseNumber}`;

                        await saveExerciseAttemptWithSession({
                            exercise_id: exerciseId,
                            user_answer: userAnswer.trim(),
                            correct_answer: currentExercise.answer,
                            is_correct: isAnswerCorrect,
                            first_attempt: true,
                            response_time_sec: Math.round(responseTime / 1000),
                            attempt_time: new Date().toISOString(),
                            category: 'Cloze Exercise',
                            subcategory: currentExercise.subcategory || 'cloze_exercise',
                            module_type: moduleType,
                            exercise_number: exerciseNumber,
                            question_text: currentExercise.question,
                            options: currentExercise.options || []
                        });

                        // Update mastery tracking
                        if (grammarId) {
                            await GrammarTracking.updateMastery(
                                user.uid,
                                parseInt(level),
                                parseInt(chapter),
                                sessionType,
                                grammarId,
                                {
                                    grammar_title: currentExercise.category || 'Grammar Exercise',
                                    exercise_id: exerciseId,
                                    is_correct: isAnswerCorrect
                                }
                            ).catch(error => {
                                console.error('✗ Failed to update grammar mastery:', error);
                            });

                            // Add question to chapter progress
                            await ChapterProgress.addQuestion(
                                user.uid,
                                parseInt(level),
                                parseInt(chapter),
                                'Cloze',
                                exerciseNumber,
                                {
                                    grammar_index: grammarId,
                                    correct_choice: [currentExercise.answer],
                                    user_choice: userAnswer.trim(),
                                    user_isCorrect: isAnswerCorrect,
                                    time_to_complete: Math.round(responseTime / 1000)
                                }
                            ).catch(error => {
                                console.error('✗ Failed to add question to chapter progress:', error);
                            });
                        }

                    } catch (fallbackError) {
                        console.error('❌ Fallback tracking also failed:', fallbackError);
                    }
                }
            };

            // Execute tracking
            trackExerciseAttempt();
            setExerciseNumber(prev => prev + 1);
        }

        // Update consecutive correct count
        if (isAnswerCorrect) {
            const newConsecutiveCorrect = consecutiveCorrect + 1;
            setConsecutiveCorrect(newConsecutiveCorrect);
        } else {
            setConsecutiveCorrect(0);
            if (currentDifficulty === 'hard') {
                setCurrentDifficulty('medium');
            } else if (currentDifficulty === 'medium') {
                setCurrentDifficulty('simple');
            }
        }

        // Show result
        setIsCorrect(isAnswerCorrect);
        setShowResult(true);

        // Auto-advance after showing result
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            if (newScore.total >= gameConfig.sessionSize) {
                endGame();
            } else {
                loadNextExercise(currentExercise.index + 1);
            }
        }, 2000);
    };

    // Handle key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && gameState === 'playing' && !showResult) {
            handleSubmit();
        }
    };

    // Navigation handlers - use navigation prop from ChapterContainer
    const _handleBackToGrammar = () => navigation?.previous();
    const handleReturnToMenu = () => navigation?.menu();
    const handleNextFeature = () => navigation?.next();

    // Early returns for loading and error states - moved after all hooks
    if (exercisesLoading) {
        return (
            <div className="cloze-exercise-container">
                <div className="cloze-content">
                    <div className="loading-spinner"></div>
                    <p>Loading exercises...</p>
                </div>
            </div>
        );
    }

    if (exercisesError) {
        return (
            <div className="cloze-exercise-container">
                <div className="cloze-content">
                    <h3 style={{ color: '#b30000' }}>❌ Error Loading Exercises</h3>
                    <p>{exercisesError.message}</p>
                    <button
                        onClick={handleReturnToMenu}
                        style={{
                            backgroundColor: '#FFA500',
                            color: 'white',
                            border: 'none',
                            padding: '1rem 2rem',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        Return to Menu
                    </button>
                </div>
            </div>
        );
    }

    if (!exercises || exercises.length === 0) {
        return (
            <div className="cloze-exercise-container">
                <div className="cloze-content">
                    <h3>📚 No Exercises Available</h3>
                    <p>Cloze exercises for Level {level}, Chapter {chapter} are not available yet.</p>
                    <button
                        onClick={handleNextFeature}
                        className="action-btn primary"
                    >
                        Continue
                    </button>
                </div>
            </div>
        );
    }

    if (!Array.isArray(exercises)) {
        return (
            <div className="cloze-exercise-container">
                <div className="cloze-content">
                    <h3 style={{ color: '#b30000' }}>❌ Invalid Data</h3>
                    <p>The exercises data structure is invalid</p>
                    <button
                        onClick={handleReturnToMenu}
                        style={{
                            backgroundColor: '#FFA500',
                            color: 'white',
                            border: 'none',
                            padding: '1rem 2rem',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        Return to Menu
                    </button>
                </div>
            </div>
        );
    }

    // Hint section renderer (must be before all render* that use it)
    const renderHintSection = (hint) => (
        <div className="hint-container">
            <button
                className="hint-button"
                onClick={() => setShowHint(!showHint)}
            >
                Hint
            </button>
            {showHint && (
                <div className="hint-text">
                    {hint}
                </div>
            )}
        </div>
    );

    // Fill blank conjugation renderer
    const renderFillBlankConjugation = (exercise, instruction, question, hint) => {
        return (
            <div className="exercise-container">
                <div className="instruction">
                    <strong>{instruction}</strong>
                </div>
                <div className="question-text">
                    {question}
                </div>
                <div className="answer-input">
                    <input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your answer..."
                        className="cloze-input standard-input"
                        autoFocus
                    />
                </div>
                {renderHintSection(hint)}
            </div>
        );
    };

    // Multiple choice renderer with radio buttons
    const renderMultipleChoice = (exercise, instruction, question, hint) => {
        const options = exercise.options || [];

        return (
            <div className="exercise-container">
                <div className="instruction">
                    <strong>{instruction}</strong>
                </div>
                <div className="question-text">
                    {question}
                </div>
                <div className="options-container">
                    {options.map((option) => (
                        <label key={option.id} className="option-label">
                            <input
                                type="radio"
                                name="multiple-choice"
                                value={option.id}
                                checked={userAnswer === option.id}
                                onChange={(e) => setUserAnswer(e.target.value)}
                            />
                            <span className="option-id">{option.id}</span>
                            <span className="option-text">{option.text}</span>
                        </label>
                    ))}
                </div>
                {renderHintSection(hint)}
            </div>
        );
    };

    // True/False renderer with radio buttons
    const renderTrueFalse = (exercise, instruction, question, hint) => {
        return (
            <div className="exercise-container">
                <div className="instruction">
                    <strong>{instruction}</strong>
                </div>
                <div className="question-text">
                    {question}
                </div>
                <div className="true-false-container">
                    <label className="true-false-label">
                        <input
                            type="radio"
                            name="true-false"
                            value="O"
                            checked={userAnswer === 'O'}
                            onChange={(e) => setUserAnswer(e.target.value)}
                        />
                        O (Correct)
                    </label>
                    <label className="true-false-label">
                        <input
                            type="radio"
                            name="true-false"
                            value="X"
                            checked={userAnswer === 'X'}
                            onChange={(e) => setUserAnswer(e.target.value)}
                        />
                        X (Incorrect)
                    </label>
                </div>
                {renderHintSection(hint)}
            </div>
        );
    };

    // Error correction renderer
    const renderErrorCorrection = (exercise, instruction, question, hint) => {
        return (
            <div className="exercise-container">
                <div className="instruction">
                    <strong>{instruction}</strong>
                </div>
                <div className="error-sentence">
                    {question}
                </div>
                <input
                    type="text"
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type the corrected sentence..."
                    className="cloze-input"
                    autoFocus
                />
                {renderHintSection(hint)}
            </div>
        );
    };

    // Open response renderer
    const renderOpenResponse = (exercise, instruction, question, hint) => {
        return (
            <div className="exercise-container">
                <div className="instruction">
                    <strong>{instruction}</strong>
                </div>
                <div className="question-text">
                    {question}
                </div>
                <div className="response-input">
                    <input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your answer..."
                        className="cloze-input"
                        autoFocus
                    />
                </div>
                {renderHintSection(hint)}
            </div>
        );
    };

    // Translation renderer
    const renderTranslation = (exercise, instruction, question, hint) => {
        return (
            <div className="exercise-container">
                <div className="instruction">
                    <strong>{instruction}</strong>
                </div>
                <div className="question-text">
                    {question}
                </div>
                <div className="translation-input">
                    <input
                        type="text"
                        value={userAnswer}
                        onChange={(e) => setUserAnswer(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type the Korean translation..."
                        className="cloze-input"
                        autoFocus
                    />
                </div>
                {renderHintSection(hint)}
            </div>
        );
    };

    // Render exercise by type (must be after all render* definitions)
    const renderExerciseByType = (exercise) => {
        if (!exercise || typeof exercise !== 'object') {
            return <div className="error-container">Invalid exercise data</div>;
        }
        const exerciseType = exercise.type || 'fill_blank_conjugation';
        const instruction = getInstructionByType(exerciseType);
        const question = exercise.question || 'Complete the sentence';
        const hint = exercise.hint || 'Think about the grammar rule';
        switch (exerciseType) {
            case 'fill_blank_conjugation':
                return renderFillBlankConjugation(exercise, instruction, question, hint);
            case 'multiple_choice':
                return renderMultipleChoice(exercise, instruction, question, hint);
            case 'true_false':
                return renderTrueFalse(exercise, instruction, question, hint);
            case 'error_correction':
                return renderErrorCorrection(exercise, instruction, question, hint);
            case 'open_response':
                return renderOpenResponse(exercise, instruction, question, hint);
            case 'translation':
                return renderTranslation(exercise, instruction, question, hint);
            default:
                return renderFillBlankConjugation(exercise, instruction, question, hint);
        }
    };

    return (
        <div className="cloze-exercise-container">
            <div className="cloze-content">
                {gameState === 'setup' && (
                    <div className="setup-screen">
                        <h2>Exercise Configuration</h2>

                        <div className="config-form">
                            <div className="config-group">
                                <label>Session Size:</label>
                                <select
                                    value={gameConfig.sessionSize}
                                    onChange={(e) => setGameConfig(prev => ({ ...prev, sessionSize: parseInt(e.target.value) }))}
                                >
                                    <option value="5">5 exercises</option>
                                    <option value="10">10 exercises</option>
                                    <option value="15">15 exercises</option>
                                </select>
                            </div>
                        </div>

                        <div className="grammar-preview">
                            <h3>Grammar Focus</h3>
                            <div className="preview-content">
                                <p><strong>Grammar Point:</strong> Progressive Form (-고 있다)</p>
                                <p><strong>Usage:</strong> Indicates an action that is currently in progress</p>
                                <p><strong>Construction:</strong> verb stem + 고 있다</p>
                                <p><strong>Speech Levels:</strong> Polite (-고 있어요), Formal (-고 있습니다), Honorific (-고 계시다)</p>
                                <p><strong>Total Exercises:</strong> {exercises.length}</p>
                            </div>
                        </div>

                        <button className="start-button" onClick={startGame}>
                            Start Exercise
                        </button>
                    </div>
                )}

                {gameState === 'playing' && currentExercise && (
                    <div className="game-screen">
                        <div className="game-header">
                            <div className="score-display">
                                Score: {score.correct}/{score.total}
                                {score.total > 0 && (
                                    <span className="accuracy">
                                        ({((score.correct / score.total) * 100).toFixed(1)}%)
                                    </span>
                                )}
                            </div>

                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${((currentExercise.index + 1) / gameConfig.sessionSize) * 100}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="question-area">
                            <div className="question-header">
                                <h3>Exercise {currentExercise.index + 1}:</h3>
                                <div className="exercise-category">
                                    <strong>{currentExercise.category}</strong>
                                </div>
                                <div className="exercise-type">
                                    Type: {currentExercise.type}
                                </div>
                            </div>

                            {renderExerciseByType(currentExercise)}

                            {!showResult && (
                                <button
                                    className="submit-button"
                                    onClick={handleSubmit}
                                    disabled={!userAnswer.trim()}
                                >
                                    Submit Answer
                                </button>
                            )}

                            {showResult && (
                                <div className="result-display">
                                    {isCorrect ? (
                                        <div className="correct-result">
                                            Correct! Well done!
                                        </div>
                                    ) : (
                                        <div className="incorrect-result">
                                            Incorrect. The correct answer was: <strong>{currentExercise.answer}</strong>
                                            {currentExercise.explanation && (
                                                <div className="explanation">
                                                    {currentExercise.explanation}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {gameState === 'completed' && (
                    <div className="completion-screen">
                        <h2>Exercise Completed!</h2>

                        <div className="final-results">
                            <div className="result-card">
                                <h3>Final Score</h3>
                                <div className="score-large">
                                    {score.correct} / {score.total}
                                </div>
                                <div className="accuracy-large">
                                    {((score.correct / score.total) * 100).toFixed(1)}% Accuracy
                                </div>
                            </div>
                        </div>

                        <div className="completion-actions">
                            <button className="module-nav-btn" onClick={handleNextFeature}>
                                <i className="fas fa-arrow-right"></i>
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClozeExercise;
